import React, { useState, useRef, useContext } from 'react'
import PropTypes from 'prop-types'
import DayPicker from 'react-day-picker'
import './style.css'
import { Context as WorldReadyContext } from '@paypalcorp/worldready-react'
import { DisplayNames } from '@paypalcorp/worldready'
import useOutsideClick from '../../../utils/hooks/useOutsideClick'

const MONTH_NUMBERS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
]

const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

function Calendar({ handleDayClick, handleOnBlur }) {
  const [selectedDay, setSelectedDay] = useState(undefined)
  const worldready = useContext(WorldReadyContext)
  const calendarRef = useRef(null)

  useOutsideClick(calendarRef, () => handleClickOutside())

  const past = { before: new Date() }

  const monthNames = new DisplayNames(worldready, {
    type: 'month',
    style: 'wide',
  })

  const weekdayNames = new DisplayNames(worldready, {
    type: 'day',
    style: 'wide',
  })

  const weekdayNamesShort = new DisplayNames(worldready, {
    type: 'day',
    style: 'abbreviated',
  })

  function getFirstDayOfWeek() {
    const firstDayName = worldready?.weekInfo?.firstDay ?? 'sun'
    const firstDayIndex = WEEKDAYS.findIndex((day) => day === firstDayName)

    if (firstDayIndex === -1) {
      return 0
    }

    return firstDayIndex
  }

  const calendar = {
    months: monthNames.select(MONTH_NUMBERS),
    weekdaysLong: weekdayNames.select(WEEKDAYS),
    weekdaysShort: weekdayNamesShort.select(WEEKDAYS),
    firstDayOfWeek: getFirstDayOfWeek(),
  }

  const handleDayFocus = (day, modifiers) => {
    // when using keyboard tab, default is focus on 1st day of month,
    // here we check if it's disabled, move focus to Today.
    if (modifiers.disabled) {
      document
        .querySelectorAll('div.DayPicker-Day.DayPicker-Day--today')[0]
        .focus()
    } else {
      setSelectedDay(day)
    }
  }

  const onDayClick = (day, disabled) => {
    setSelectedDay(day)
    handleDayClick(day, disabled)
  }

  function handleClickOutside() {
    if (handleOnBlur) {
      handleOnBlur()
    }
  }

  return (
    <div
      data-testid="calendar-container"
      className="calendar-container"
      ref={calendarRef}
    >
      <div id="triangle" className="top-left" />
      <div className="calendar-day-picker">
        <DayPicker
          showOutsideDays
          disabledDays={past}
          onDayClick={onDayClick}
          onDayFocus={handleDayFocus}
          months={calendar.months}
          weekdaysLong={calendar.weekdaysLong}
          weekdaysShort={calendar.weekdaysShort}
          firstDayOfWeek={calendar.firstDayOfWeek}
          selectedDays={selectedDay}
        />
      </div>
    </div>
  )
}

Calendar.propTypes = {
  handleDayClick: PropTypes.func.isRequired,
  handleOnBlur: PropTypes.func,
}

export default Calendar
